import React, { useCallback } from "react";
import { useRouter } from "next/router";
import * as yup from "yup";
import * as TE from "fp-ts/lib/TaskEither";
import styled from "@emotion/styled";

import Button from "@components/common/Button";
import { Input, Form, useForm } from "@components/common/Input";
import { Spacer } from "@components/common/Spacer";

interface Response {
  errors: string[];
  email: string;
}

const schema = yup.object({
  email: yup.string().required().email(),
});

export const SubmitButton = styled(Button)`
  width: 100%;
`;

const initialState = {
  email: "",
};

// We can create a separated file for handling this url's
const fetchUrl =
  process.env.NODE_ENV === "test"
    ? "http://localhost:3000/api/account/forgot-password"
    : "/api/account/forgot-password";

const forgotPassword = async (
  form: { email: string },
  callback: () => void,
): Promise<Response> => {
  if (form.email.includes("accenture.com")) {
    throw new Error("Accenture accounts cannot reset your password");
  }
  const request = await fetch(fetchUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(form),
  });

  // @ts-ignore
  const response: Response = await request.json();
  if (response.errors.length > 0) {
    throw new Error(response.errors[0]);
  }

  callback();

  return response;
};

export const ForgotPasswordForm = () => {
  const router = useRouter();

  const callback = useCallback(
    () => router.push("/login?message=PASS_SENT"),
    [],
  );

  const { useField, handleSubmit, formErrors, status } = useForm(
    initialState,
    schema,
    (form) =>
      TE.tryCatch(
        (): Promise<Response> => forgotPassword(form, callback),
        (e: any) => (e?.message ? { email: [e.message] } : { formErrors: [] }),
      ),
  );

  const email = useField("email");

  return (
    <Form onSubmit={handleSubmit} errors={formErrors}>
      <Input {...email} type="email" ariaLabel="email" label="Email" />
      <Spacer size="xg" />
      <Button block color="blue" disabled={status !== "VALID"}>
        Reset Password
      </Button>
    </Form>
  );
};
